import { Organization } from "@/data/Organization";
import { CurrentUser } from "@/data/users/CurrentUser";
import { AppPages } from "@/router";
import { AppBar as MuiAppBar, SxProps } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import { Theme } from "klayowebshared";
import React from "react";
import { Location } from "react-router-dom";
import { AppContext } from "../common/AppContext";
import { EventBus } from "../common/EventBus";
import BellIcon from "../resources/images/icons-bell.svg";
import MenuIcon from "../resources/images/icons-menu.svg";
import Logo from "../resources/images/logo.svg";
import { AppNav } from "./AppNav";
import { Button } from "./Button";
import { Tooltip } from "./Tooltip";

interface AppBarProps {
  allowThemeChange?: boolean;
  showNavigation?: boolean;
  unreadNotifications?: number | null;
  onNavigation?: (e: React.MouseEvent, callback: any) => void;
  onSignOut?: (e: React.MouseEvent) => void;
  onLogoClick?: (e: React.MouseEvent) => void;
  onShowSwitchUserDialog?: (e: React.MouseEvent) => void;
  onOpenDevDialog?: (e: React.MouseEvent) => void;
  onOpenSettings?: (e: React.MouseEvent) => void;
  onDemoSwitch?: (e: React.MouseEvent, demo: any) => void;
  onMenuButtonClick?: (e: React.MouseEvent) => void;
  onSwitchTheme?: (themeName: string) => void;
  themeName?: string;
  theme?: typeof Theme;
  user?: CurrentUser | null;
  organization?: Organization | null;
  navItems?: Partial<(typeof AppPages)[keyof typeof AppPages]>[];
  defaultNavItem?: Partial<(typeof AppPages)[keyof typeof AppPages]>;
  location?: Location;
  sx?: SxProps;
  showActions?: boolean;
  onShowNotifications?: (e: React.MouseEvent) => void;
  onOpenDashboard?: (e: React.MouseEvent) => void;
  onSignIn?: (e: React.MouseEvent) => void;
}
interface AppBarState {
  userButton: any;
  showActions: boolean;
  unreadNotifications?: number | null;
  profilePicUpdated: boolean;
}
export class AppBar extends React.Component<AppBarProps, AppBarState> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static defaultProps = {
    allowThemeChange: false,
    showNavigation: false
  };

  constructor(props: AppBarProps) {
    super(props);

    this.state = {
      userButton: null,
      showActions: true,
      unreadNotifications: this.props.unreadNotifications,
      profilePicUpdated: false
    };
  }

  componentDidMount() {
    this.context.eventBus?.on(EventBus.event.profilePictureChanged, () => {
      this.setState({ profilePicUpdated: true });
    });
  }

  onUserButton(e: React.MouseEvent) {
    this.setState({ userButton: e.target });
  }

  onUserMenuClose(e: React.MouseEvent) {
    this.setState({ userButton: null });
  }

  onNavigation(e: any, callback: any) {
    const { onNavigation } = this.props;
    return onNavigation && onNavigation(e, callback);
  }

  onSignOut(e: React.MouseEvent) {
    this.onUserMenuClose(e);

    const { onSignOut } = this.props;
    if (onSignOut) this.onNavigation(e, onSignOut);
  }

  onLogoClick(e: React.MouseEvent) {
    const { onLogoClick } = this.props;
    if (onLogoClick) this.onNavigation(e, onLogoClick);
  }

  onShowSwitchUserDialog(e: React.MouseEvent) {
    this.onUserMenuClose(e);

    const { onShowSwitchUserDialog } = this.props;
    if (onShowSwitchUserDialog) this.onNavigation(e, onShowSwitchUserDialog);
  }

  onNotificationReceived(notif: any, numUnread: any) {
    this.setUnreadNotifications(numUnread);
  }

  setUnreadNotifications(numUnread: any) {
    this.setState({ unreadNotifications: numUnread });
  }

  onSwitchTheme() {
    this.setState({ userButton: null }, () => {
      this.props.onSwitchTheme?.(this.props.themeName === "v1" ? "v2" : "v1");
    });
  }

  onOpenDevDialog(e: React.MouseEvent) {
    this.onUserMenuClose(e);

    const { onOpenDevDialog } = this.props;
    if (onOpenDevDialog) this.onNavigation(e, onOpenDevDialog);
  }

  onOpenSettings(e: React.MouseEvent) {
    this.onUserMenuClose(e);

    const { onOpenSettings } = this.props;
    if (onOpenSettings) this.onNavigation(e, onOpenSettings);
  }

  onDemoSwitch(e: React.MouseEvent, demo: any) {
    this.onUserMenuClose(e);

    const { onDemoSwitch } = this.props;
    if (onDemoSwitch) this.onNavigation(e, (e: React.MouseEvent) => onDemoSwitch(e, demo));
  }

  onMenuButtonClick(e: React.MouseEvent) {
    this.props.onMenuButtonClick?.(e);
  }

  render() {
    const {
      user,
      organization,
      theme,
      showNavigation,
      showActions,
      onShowNotifications,
      navItems,
      defaultNavItem,
      location,
      onNavigation,
      sx
    } = this.props;
    const { unreadNotifications } = this.state;

    return (
      <MuiAppBar
        className='klaro-appbar'
        position='fixed'
        elevation={0}
        sx={{
          ...sx,
          background: "var(--white-wh-900)",
          borderBottom: "1px solid var(--neon-blue-nb-050)",
          padding: {
            xs: "0 16px 0 10px",
            sm: "0 16px 0 10px",
            md: "0 40px",
            lg: "0 40px"
          }
        }}
      >
        <Toolbar className='klaro-appbar_toolbar'>
          <Button
            className='klaro-appbar_menubutton klayo-appbar_button'
            icon={<MenuIcon />}
            size='sm'
            // tooltip='Menu'
            onClick={this.onMenuButtonClick.bind(this)}
            sx={{ flexGrow: 1, md: { display: "none" } }}
          />

          <Box
            className='klaro-appbar_logocontainer'
            onClick={this.onLogoClick.bind(this)}
            sx={{
              height: {
                xs: "24px!important",
                sm: "32px!important"
              }
            }}
          >
            <Logo className='klaro-appbar_logo' height='100%' />
            {organization && organization.isDemo && (
              <Tooltip title='Demo mode' placement='right'>
                <div className='klaro-appbar_logo_demo'>Demo Company</div>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            {showNavigation && (
              <AppNav
                sx={{ flexGrow: 1, [theme.breakpoints.down("md")]: { display: "none" } }}
                location={location}
                user={this.props.user}
                defaultItem={defaultNavItem}
                onNavigation={onNavigation}
                items={navItems}
              />
            )}
          </Box>

          {showActions && (
            <Box>
              <Stack direction='row' spacing={2}>
                {user && (
                  <Button
                    className='klayo-appbar_notificationbutton klayo-appbar_button'
                    size='sm'
                    ariaLabel='Notifications'
                    icon={<BellIcon />}
                    badgeProps={{
                      badgeContent: unreadNotifications,
                      invisible: unreadNotifications === null,
                      variant: "dot",
                      color: "error",
                      max: 99
                    }}
                    color='inherit'
                    tooltip={
                      "Unread notifications: " + (unreadNotifications ? unreadNotifications : 0)
                    }
                    onClick={onShowNotifications}
                  />
                )}
              </Stack>
            </Box>
          )}
        </Toolbar>
      </MuiAppBar>
    );
  }
}
